import { Grid } from '@mui/material';
import { useCallback, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import {
  SearchBarNew,
  WsAutoComplete,
  WsColors,
  WsTypoColors,
  WsTypography,
  WsTypoVariants,
} from 'wisestep-base';

import { ClientAutocomplete } from '../../../../components/ClientAutocomplete';
import {
  IFilters,
  contactsDefaultFilterValues,
} from '../../../../features/clientContacts/helper/ContactListingHelper';
import { StyledBox } from '../../../../features/clientListingPage/helper/ClientListingHelper';
import { getFilterValue } from '../../../../features/notes/helper/NotesHelper';
import SmartFilter from '../../../../features/submissionsNew/components/filters/smart/SmartFilter';

export interface IContactFilters {
  searchQuery: IFilters;
  clear: () => void;
  getUpdatedFiltersFromFilters: (data) => void;
  tags: string[];
  contactsCount?: number;
  onAddButtonClick?: () => void;
  isClientContactTab?: boolean;
}

const ContactFilters = ({
  searchQuery,
  clear,
  getUpdatedFiltersFromFilters,
  tags,
  isClientContactTab = false,
}: IContactFilters) => {
  const methods = useForm<IFilters>({
    defaultValues: searchQuery ?? contactsDefaultFilterValues,
  });
  const { handleSubmit, getValues, setValue, reset } = methods;
  const onSearch = () => handleFilters();
  const [status, setStatus] = useState<string[]>(searchQuery.status ?? []);
  const [contactTags, setContactTags] = useState<string[]>();

  const handleClear = () => {
    setContactTags([]);
    setStatus(['Active']);
    reset(contactsDefaultFilterValues);
    clear();
  };

  const handleFilters = useCallback(async () => {
    const updatedQuery = {
      ...searchQuery,
      search: getValues('search') ?? '',
      status: getValues('status') ?? [],
      tags: getValues('tags') ?? [],
      clients: getValues('clients') ?? [],
    };
    getUpdatedFiltersFromFilters(updatedQuery);
  }, [searchQuery, getUpdatedFiltersFromFilters, getValues]);

  const onStatusSelection = async (statusInput: string[]) => {
    setStatus(statusInput);
    setValue('status', statusInput);
  };

  const onTagsSelection = async (tagsInput: string[]) => {
    setContactTags(tagsInput);
    setValue('tags', tagsInput);
  };

  return (
    <StyledBox sx={{ border: 'none' }}>
      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(onSearch)}>
          <Grid container bgcolor={WsColors.White}>
            <Grid item xs={4}>
              <SearchBarNew
                size="small"
                name="search"
                placeholder="Search Contacts"
                type="submit"
                onClear={handleClear}
              />
            </Grid>
            <Grid container justifyContent="flex-start" alignItems="center" pt={2} spacing={1}>
              <Grid item>
                <WsTypography variant={WsTypoVariants.SUB_TITLE} bold color={WsTypoColors.BLACK}>
                  Applied Filter:
                </WsTypography>
              </Grid>
              <Grid item>
                <SmartFilter
                  filterName="Client Contact Status :"
                  onClick={onSearch}
                  filterValue={getFilterValue('status', methods)}
                >
                  <WsAutoComplete
                    onTagsChange={onStatusSelection}
                    placeholder="Eg, Michael"
                    initialValue={status}
                    optionsInStringArray={['All', 'Active', 'Inactive']}
                    value={status}
                    multiple={true}
                    freeSolo={true}
                    name="status"
                    size="small"
                  />
                </SmartFilter>
              </Grid>
              {!isClientContactTab && (
                <Grid item>
                  <SmartFilter
                    filterName="Client :"
                    onClick={onSearch}
                    filterValue={getFilterValue('clients', methods)}
                  >
                    <ClientAutocomplete name="clients" multiple />
                  </SmartFilter>
                </Grid>
              )}
              <Grid item>
                <SmartFilter
                  filterName="Client Contact Tags :"
                  onClick={onSearch}
                  filterValue={getFilterValue('tags', methods)}
                >
                  <WsAutoComplete
                    onTagsChange={onTagsSelection}
                    placeholder="Eg, Michael"
                    initialValue={contactTags ?? []}
                    value={contactTags}
                    optionsInStringArray={tags}
                    multiple={true}
                    freeSolo={true}
                    name="tags"
                    size="small"
                  />
                </SmartFilter>
              </Grid>
            </Grid>
          </Grid>
        </form>
      </FormProvider>
    </StyledBox>
  );
};

export default ContactFilters;
